import React, { useState, useEffect } from 'react';
import { useAppTheme } from '../theme/AppThemeContext.jsx';
import { Box, Typography, Button, Fab } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import WeekView from './features/WeekView.jsx';
import MultiDayView from './features/MultiDayView.jsx';
import { CalendarDayStripWithTasks } from './features/CalendarDayStripWithTasks.jsx';
import TaskDialog from './features/TaskDialog.jsx';
import CalendarHeaderBar from './features/CalendarHeaderBar.jsx';
import { useTasks } from '../hooks/useTasks.js';

function Calendar({ user, onLogout }) {
  const { tokens } = useAppTheme();
  const [currentDate, setCurrentDate] = useState(new Date());

  const [viewMode, setViewMode] = useState('day'); // month, week, day
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);

  // Use the real useTasks hook
  const { 
    tasks, 
    loading, 
    error, 
    addTask, 
    updateTask, 
    deleteTask, 
    toggleTaskComplete,
    recurringUpdateDialog,
    handleRecurringUpdateChoice,
    closeRecurringUpdateDialog,
    recurringDeleteDialog,
    handleRecurringDeleteChoice,
    closeRecurringDeleteDialog
  } = useTasks(user, currentDate, {
    onAddTask: (gapInfo) => {
      // Pre-fill task dialog with gap information
      setTaskToEdit({
        startTime: gapInfo.suggestedStartTime,
        endTime: gapInfo.suggestedEndTime,
        date: gapInfo.date,
      });
      setTaskDialogOpen(true);
    }
  });

  const handleTasksLoaded = (_taskCount) => {};

  const handleToggleDone = (taskId, completed) => {
    toggleTaskComplete(taskId, completed);
  };

  const handleTaskClick = (task) => {
    setTaskToEdit(task);
    setTaskDialogOpen(true);
  };

  const handleAddTask = () => {
    setTaskToEdit(null);
    setTaskDialogOpen(true);
  };

  const handleCreateTask = async (taskData) => {
    try {
      await addTask(taskData);
      setTaskDialogOpen(false);
      setTaskToEdit(null);
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleUpdateTask = async (taskId, updateData) => {
    try {
      await updateTask(taskId, updateData);
      setTaskDialogOpen(false);
      setTaskToEdit(null);
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      setTaskDialogOpen(false);
      setTaskToEdit(null);
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleTaskDialogClose = () => {
    setTaskDialogOpen(false);
    setTaskToEdit(null);
  };

  const handleDateChange = (newDate) => {
    setCurrentDate(newDate);
  };

  const handlePickMonth = (monthYear) => {
    const newDate = new Date(`${monthYear.month} 1, ${monthYear.year}`);
    setCurrentDate(newDate);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Calendar Header */}
      <CalendarHeaderBar
        selectedDate={currentDate}
        viewMode={viewMode}
        user={user}
        onPickMonth={handlePickMonth}
        onChangeViewMode={(mode) => {
          setViewMode(mode);
        }}
        onDateChange={handleDateChange}
        onAddTask={handleAddTask}
        onLogout={onLogout}
      />

      {/* Calendar Content */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flex: 1 
          }}>
            <Typography sx={{ color: 'text.secondary' }}>Loading tasks...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flex: 1 
          }}>
            <Typography sx={{ color: 'error' }}>{error}</Typography>
          </Box>
        ) : viewMode === 'day' ? (
          <CalendarDayStripWithTasks
            user={user}
            weekStart={currentDate}
            initialSelected={currentDate}
            onToggleDone={handleToggleDone}
            onTaskClick={handleTaskClick}
            onTasksLoaded={handleTasksLoaded}
          />
        ) : viewMode === 'week' ? (
          <WeekView
            user={user}
            weekStart={currentDate}
            onToggleDone={handleToggleDone}
            onTaskClick={handleTaskClick}
            onTasksLoaded={handleTasksLoaded}
          />
        ) : viewMode === 'multiday' ? (
          <MultiDayView
            user={user}
            startDate={currentDate}
            tasks={tasks}
            onTaskClick={handleTaskClick}
            onTasksLoaded={handleTasksLoaded}
          />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flex: 1 
          }}>
            <Typography variant="body2" color="text.secondary">
              {viewMode.charAt(0).toUpperCase() + viewMode.slice(1)} view coming soon!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Day, Week, and Multi-Day views are fully functional. Month view will be added next.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={() => {
          setTaskToEdit(null);
          setTaskDialogOpen(true);
        }}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: tokens.primary,
          '&:hover': {
            background: tokens.primaryHover,
          },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Task Dialog */}
      <TaskDialog
        open={taskDialogOpen}
        onClose={handleTaskDialogClose}
        onAddTask={handleCreateTask}
        onUpdateTask={handleUpdateTask}
        onDeleteTask={handleDeleteTask}
        taskToEdit={taskToEdit}
        user={user}
        existingTasks={tasks}
        recurringUpdateDialog={recurringUpdateDialog}
        handleRecurringUpdateChoice={handleRecurringUpdateChoice}
        closeRecurringUpdateDialog={closeRecurringUpdateDialog}
        recurringDeleteDialog={recurringDeleteDialog}
        handleRecurringDeleteChoice={handleRecurringDeleteChoice}
        closeRecurringDeleteDialog={closeRecurringDeleteDialog}
      />
    </Box>
  );
}

export default Calendar;
